/* eslint-disable prettier/prettier */
import { useState, useCallback, useEffect } from 'react';
import {
	faqService,
	FAQArticle,
	FAQFilters,
	CreateFAQRequest,
} from '../services/faqService';

// Hook for fetching all FAQ articles with filters
export const useFAQArticles = (filters?: FAQFilters) => {
	const [articles, setArticles] = useState<FAQArticle[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchArticles = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		try {
			const data = await faqService.getFAQArticles(filters);
			setArticles(data || []);
		} catch (err: any) {
			setError(err.message || 'An error occurred while fetching FAQs');
		} finally {
			setIsLoading(false);
		}
	}, [JSON.stringify(filters)]); // Dependency on stringified filters to prevent infinite loops

	useEffect(() => {
		fetchArticles();
	}, [fetchArticles]);

	return { articles, isLoading, error, refreshArticles: fetchArticles };
};

// Hook for a single FAQ Detail
export const useFAQDetails = (articleId: string) => {
	const [article, setArticle] = useState<FAQArticle | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchDetails = useCallback(async () => {
		if (!articleId) return;
		setIsLoading(true);
		setError(null);
		try {
			const data = await faqService.getFAQById(articleId);
			setArticle(data);
		} catch (err: any) {
			setError(
				err.message || 'An error occurred while fetching FAQ details'
			);
		} finally {
			setIsLoading(false);
		}
	}, [articleId]);

	useEffect(() => {
		fetchDetails();
	}, [fetchDetails]);

	// Added a helper for the helpful feedback directly in the hook
	const submitFeedback = async (helpful: boolean) => {
		try {
			await faqService.markHelpful(articleId, helpful);
			// Optionally refresh details to see updated counts
			fetchDetails();
			return { success: true };
		} catch (err: any) {
			return { success: false, message: err.message };
		}
	};

	return {
		article,
		isLoading,
		error,
		refreshDetails: fetchDetails,
		submitFeedback,
	};
};

export const useFAQActions = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const createFAQ = useCallback(async (data: CreateFAQRequest) => {
		setIsLoading(true);
		setError(null);
		setSuccess(false);

		try {
			const id = await faqService.createFAQArticle(data);
			setSuccess(true);
			return { success: true, id };
		} catch (err: any) {
			const message = err.message || 'Failed to create FAQ';
			setError(message);
			return { success: false, message };
		} finally {
			setIsLoading(false);
		}
	}, []);

	const updateFAQ = useCallback(
		async (articleId: string, data: Partial<CreateFAQRequest>) => {
			setIsLoading(true);
			setError(null);
			setSuccess(false);

			try {
				await faqService.updateFAQArticle(articleId, data);
				setSuccess(true);
				return { success: true };
			} catch (err: any) {
				const message = err.message || 'Failed to update FAQ';
				setError(message);
				return { success: false, message };
			} finally {
				setIsLoading(false);
			}
		},
		[]
	);

	return {
		createFAQ,
		updateFAQ,
		isLoading,
		error,
		success,
	};
};
